export const EXPENSE_CATEGORIES = [
  { value: 'activities', label: 'Activities & Sports' },
  { value: 'medical', label: 'Medical & Dental' },
  { value: 'school', label: 'School & Education' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'food', label: 'Food & Dining' },
  { value: 'childcare', label: 'Childcare' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'other', label: 'Other' }
];

export const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

export const DAYS_OF_WEEK_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const SETTLEMENT_METHODS = [
  { value: 'venmo', label: 'Venmo' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' }
];

export const RECURRING_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'weekly', label: 'Weekly' }
];

export const EVENT_CATEGORIES = [
  { value: 'medical', label: 'Medical' },
  { value: 'school', label: 'School' },
  { value: 'social', label: 'Social' },
  { value: 'other', label: 'Other' }
];

export const GOOGLE_CALENDAR_SYNC_MODES = [
  { value: 'custody', label: 'Custody Schedule Only' },
  { value: 'activities', label: 'Activities & Events Only' },
  { value: 'both', label: 'Custody + Activities' }
];

export const DOCUMENT_CATEGORIES = [
  { value: 'medical', label: 'Medical Records' },
  { value: 'school', label: 'School Documents' },
  { value: 'legal', label: 'Legal Documents' },
  { value: 'id_insurance', label: 'ID & Insurance' },
  { value: 'other', label: 'Other' }
];

export const ALLOWED_FILE_TYPES = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/heic': '.heic',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// Shared reaction set for message reactions (web + mobile use the same list).
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// ─── Message linking ────────────────────────────────────────────────────────
//
// A thread can be "about" one other record in the app — an expense, an
// activity, or a custody swap. The link lives on the THREAD rather than on an
// individual message so the whole conversation stays attached to the thing it
// concerns, and so "Discuss" from an expense reopens the existing conversation
// instead of starting a new one every time.
//
// `linkedKey` (see messageService.linkedEntityKey) is the flat "type:id" string
// the thread is actually queried by. Two equality filters would work in
// Firestore, but a single key needs no composite index and no index deploy.

export const MESSAGE_LINK_TYPES = {
  expense: {
    type: 'expense',
    label: 'Expense',
    // Route/screen each platform opens when the context banner is tapped.
    webPath: '/expenses',
    mobileTab: 'Expenses',
    mobileScreen: 'ExpensesList',
  },
  activity: {
    type: 'activity',
    label: 'Activity',
    webPath: '/activities',
    mobileTab: 'More',
    mobileScreen: 'Activities',
  },
  swap: {
    type: 'swap',
    label: 'Swap Request',
    webPath: '/swaps',
    mobileTab: 'More',
    mobileScreen: 'Swaps',
  },
};

// ─── Quiet hours ────────────────────────────────────────────────────────────
//
// Quiet hours suppress the PUSH, never the message. The message still lands in
// the thread at the moment it was sent, keeping the conversation record
// truthful — holding delivery would put a misleading timestamp into something
// that may later be read as evidence. A parent who wants the message itself to
// arrive later schedules it explicitly instead.
//
// Hours are 0-23 in the RECIPIENT's local timezone (users/{uid}.timezone).
// A window may wrap midnight (start 21, end 8), which is the common case.

export const DEFAULT_QUIET_HOURS = {
  enabled: false,
  startHour: 21,
  endHour: 8,
};

// Preset delays offered in the "send later" menu, resolved against the
// sender's clock at compose time.
export const SCHEDULED_SEND_PRESETS = [
  { key: 'morning', label: 'Tomorrow morning', hour: 8, dayOffset: 1 },
  { key: 'evening', label: 'This evening', hour: 18, dayOffset: 0 },
  { key: 'in1h', label: 'In 1 hour', offsetMinutes: 60 },
];

export const CHORE_FREQUENCIES = [
  { value: 'daily', label: 'Every Day' },
  { value: 'specificDays', label: 'Specific Days' },
  { value: 'weekly', label: 'Once a Week' },
];

export const CHORE_SUGGESTIONS = [
  { name: 'Make bed', ageRange: '5+' },
  { name: 'Put away toys', ageRange: '3+' },
  { name: 'Set the table', ageRange: '5+' },
  { name: 'Clear dishes', ageRange: '6+' },
  { name: 'Take out trash', ageRange: '8+' },
  { name: 'Feed pet', ageRange: '6+' },
  { name: 'Fold laundry', ageRange: '7+' },
  { name: 'Vacuum room', ageRange: '8+' },
  { name: 'Load dishwasher', ageRange: '9+' },
  { name: 'Pack school bag', ageRange: '6+' },
  { name: 'Brush teeth', ageRange: '3+' },
  { name: 'Pick up room', ageRange: '4+' },
];

export const RULE_CATEGORIES = [
  { value: 'screen_time', label: 'Screen Time' },
  { value: 'bedtime', label: 'Bedtime & Sleep' },
  { value: 'homework', label: 'Homework & Study' },
  { value: 'diet', label: 'Diet & Nutrition' },
  { value: 'discipline', label: 'Discipline' },
  { value: 'social', label: 'Social & Friends' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'other', label: 'Other' },
];

export const RULE_STATUSES = {
  proposed: { label: 'Proposed', color: 'amber' },
  agreed: { label: 'Agreed', color: 'green' },
  'needs-discussion': { label: 'Needs Discussion', color: 'red' },
};

// Self-reported acquisition source shown on the signup screen. Values are
// stored verbatim on users/{uid}.foundVia and bucketed by the metrics rollup
// into platformStats.newAccountsByFoundVia. Kept distinct from the auto-derived
// `signupSource` (UTM / referrer). The admin FoundViaChart owns the display set.
export const FOUND_VIA_OPTIONS = [
  { value: 'google', label: 'Google search' },
  { value: 'blog', label: 'Blog post' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'threads', label: 'Threads' },
  { value: 'friend', label: 'Friend / co-parent' },
  { value: 'app_store', label: 'App Store' },
];

export const DEFAULT_CHILD_COLORS = [
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#10b981', name: 'Emerald' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#84cc16', name: 'Lime' },
];
